import React, { useState } from 'react';

// Reusable InputField component for consistency
const InputField = ({ label, name, value, onChange, type = 'text', step, readOnly = false }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
        <input
            type={type}
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            step={step}
            readOnly={readOnly}
            className={`mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${readOnly ? 'bg-gray-100' : ''}`}
        />
    </div>
);

// Reusable SelectField component for enums
const SelectField = ({ label, name, value, onChange, options }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
        <select
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
            {options.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    </div>
);


// Reusable Section component to group fields
const Section = ({ title, children }) => (
    <div className="p-4 border rounded-lg bg-gray-50 mb-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {children}
        </div>
    </div>
);

const ManualRecordForm = ({ onManualCreate, collectors, assignedCollector, setAssignedCollector }) => {
    // State to hold all form data
    const [formData, setFormData] = useState({
        provider: '',
        renderingFacility: '',
        taxId: '',
        ptName: '',
        dob: '',
        ssn: '',
        employer: '',
        doi: [{ value: '' }],
        adjNumber: [{ value: '' }],
        insurance: '',
        bill: '',
        paid: '', // New
        outstanding: '', // New
        fds: '',
        lds: '',
        solDate: '',
        ledger: 'not required', // New
        hcf: 'not required', // New
        invoice: 'not required', // New
        signinSheet: 'not required', // New
        hearingStatus: '',
        hearingDate: '',
        hearingTime: '',
        judgeName: '',
        courtRoomlink: '',
        judgePhone: '',
        AccesCode: '',
        boardLocation: '',
        lienStatus: '',
        caseStatus: '', // Default to empty string for the new dropdown
        caseDate: '',
        crAmount: '',
        dorFiledBy: '',
        status4903_8: '',
        pmrStatus: '',
        judgeOrderStatus: '',
        adjuster: '',
        adjusterPhone: '',
        adjusterFax: '',
        adjusterEmail: '',
        defenseAttorney: '',
        defenseAttorneyPhone: '',
        defenseAttorneyFax: '',
        defenseAttorneyEmail: '',
        claimNo: [{ value: '' }],
    });

    // Options for documentation dropdowns
    const docOptions = [
        { value: 'not required', label: 'Not Required' },
        { value: 'yes', label: 'yes' },
        { value: 'no', label: 'no' },

    ];
    const customDropdownOptions = {
        'dorFiledBy': [
            { value: '', label: 'Select Filer' },
            { value: 'HUBUR', label: 'HUBUR' },
            { value: 'CLIENT', label: 'CLIENT' },
            { value: 'ANOTHER LIEN CLAIMANT', label: 'ANOTHER LIEN CLAIMANT' }
        ],
        'status4903_8': [
            { value: '', label: 'Select Status' },
            { value: 'yes', label: 'yes' },
            { value: 'no', label: 'no' }
        ],
        'pmrStatus': [
            { value: '', label: 'Select Status' },
            { value: 'yes', label: 'yes' },
            { value: 'no', label: 'no' }
        ],
        'judgeOrderStatus': [
            { value: '', label: 'Select Status ' },
            { value: 'GRANTED', label: 'GRANTED' },
            { value: 'PENDING', label: 'PENDING' }
        ],
        'hearingTime': [
            { value: '', label: 'Select Time' },
            { value: 'AM', label: 'AM' },
            { value: 'PM', label: 'PM' }
        ]
    };

    // NEW: Options for Case Status dropdown
    const caseStatusOptions = [
        { value: '', label: 'Select status (default)' },
        { value: 'SETTLED', label: 'SETTLED' },
        { value: 'C & R (GRANTED)', label: 'C & R (GRANTED)' },
        { value: 'CIC PENDING', label: 'CIC PENDING' },
        { value: 'A & S GRANTED', label: 'A & S GRANTED' },
        { value: 'ADR CASE - SETTED AND PAID ADR', label: 'ADR CASE - SETTED AND PAID ADR' },
        { value: 'ORDER OF DISMISAAL OF CASE', label: 'ORDER OF DISMISAAL OF CASE' },
    ];

    // NEW: Options for Lien Status dropdown
    const lienStatusOptions = [
        { value: '', label: 'Select status (default)' },
        { value: 'Not Filed', label: 'Not Filed' },
        { value: 'Out of SOL', label: 'Out of SOL' },
        { value: 'Filed', label: 'Filed' },
        { value: 'Settled', label: 'Settled' },
    ];

    // Handler for simple input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newFormData = { ...prev, [name]: value };

            // --- Auto-calculate Outstanding ---
            let bill = name === 'bill' ? parseFloat(value) : parseFloat(prev.bill);
            let paid = name === 'paid' ? parseFloat(value) : parseFloat(prev.paid);

            bill = isNaN(bill) ? 0 : bill;
            paid = isNaN(paid) ? 0 : paid;

            if (name === 'bill' || name === 'paid') {
                newFormData.outstanding = (bill - paid).toFixed(2);
            }
            // --- End Auto-calculate ---

            if (name === 'lds' && value) {
                const ldsDate = new Date(value);
                if (!isNaN(ldsDate.getTime())) {
                    // Add 18 months
                    ldsDate.setMonth(ldsDate.getMonth() + 18);

                    // Format to YYYY-MM-DD for the input field
                    const year = ldsDate.getFullYear();
                    const month = String(ldsDate.getMonth() + 1).padStart(2, '0');
                    const day = String(ldsDate.getDate()).padStart(2, '0');

                    newFormData.solDate = `${year}-${month}-${day}`;
                }
            } else if (name === 'lds' && !value) {
                // Clear SOL Date if LDS is cleared
                newFormData.solDate = '';
            }

            return newFormData;
        });
    };

    // Handler for fields that can have multiple entries
    const handleMultiEntryChange = (index, field, value) => {
        const updatedEntries = [...formData[field]];
        updatedEntries[index].value = value;
        setFormData(prev => ({ ...prev, [field]: updatedEntries }));
    };

    // Function to add a new input for a multi-entry field
    const addMultiEntry = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: [...prev[field], { value: '' }]
        }));
    };

    // Function to remove an input from a multi-entry field
    const removeMultiEntry = (index, field) => {
        const updatedEntries = [...formData[field]];
        updatedEntries.splice(index, 1);
        setFormData(prev => ({ ...prev, [field]: updatedEntries }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Create a copy of the form data to transform
        const payload = { ...formData };

        // Helper function to parse numeric fields
        const parseNumeric = (value) => {
            if (value === null || value === undefined || value === '') {
                return null;
            }
            const num = parseFloat(value);
            return isNaN(num) ? null : num;
        };

        // Helper function to parse Enum/Dropdown fields
        // This converts empty strings "" to null so the backend doesn't reject them
        const parseEnum = (value) => {
            if (value === '' || value === 'Select Status' || value === 'Select Filer' || value === 'Select Time') {
                return null;
            }
            return value;
        };

        // Convert numeric fields
        payload.bill = parseNumeric(formData.bill);
        payload.paid = parseNumeric(formData.paid);
        payload.outstanding = parseNumeric(formData.outstanding);
        payload.crAmount = parseNumeric(formData.crAmount);

        // Convert Enum fields (Fix for your error)
        payload.dorFiledBy = parseEnum(formData.dorFiledBy);
        payload.status4903_8 = parseEnum(formData.status4903_8);
        payload.pmrStatus = parseEnum(formData.pmrStatus);
        payload.judgeOrderStatus = parseEnum(formData.judgeOrderStatus);
        payload.hearingTime = parseEnum(formData.hearingTime);
        payload.caseStatus = parseEnum(formData.caseStatus);
        payload.lienStatus = parseEnum(formData.lienStatus); // NEW: Parse Lien Status

        // Send the transformed payload
        onManualCreate(payload);
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Create New Record Manually</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <Section title="Provider Information">
                    <InputField label="Provider Name" name="provider" value={formData.provider} onChange={handleChange} />
                    <InputField label="Rendering Facility" name="renderingFacility" value={formData.renderingFacility} onChange={handleChange} />
                    <InputField label="Tax ID" name="taxId" value={formData.taxId} onChange={handleChange} />

                </Section>
                <Section title="Patient & Billing Information">
                    <InputField label="Patient Name" name="ptName" value={formData.ptName} onChange={handleChange} />
                    <InputField label="Date of Birth" name="dob" type="date" value={formData.dob} onChange={handleChange} />
                    <InputField label="SSN" name="ssn" value={formData.ssn} onChange={handleChange} />
                    <InputField label="Employer" name="employer" value={formData.employer} onChange={handleChange} />
                    <InputField label="Insurance" name="insurance" value={formData.insurance} onChange={handleChange} />
                    <InputField label="Bill Amount" name="bill" type="number" step="0.01" value={formData.bill} onChange={handleChange} />
                    <InputField label="Paid Amount" name="paid" type="number" step="0.01" value={formData.paid} onChange={handleChange} />
                    <InputField label="Outstanding" name="outstanding" type="number" step="0.01" value={formData.outstanding} onChange={() => { }} readOnly={true} />
                </Section>

                <Section title="Documentation Status">
                    <SelectField label="Ledger" name="ledger" value={formData.ledger} onChange={handleChange} options={docOptions} />
                    <SelectField label="HCF" name="hcf" value={formData.hcf} onChange={handleChange} options={docOptions} />
                    <SelectField label="Invoice" name="invoice" value={formData.invoice} onChange={handleChange} options={docOptions} />
                    <SelectField label="Signin Sheet" name="signinSheet" value={formData.signinSheet} onChange={handleChange} options={docOptions} />
                </Section>

                <Section title="Service & Case Dates">
                    <InputField label="First Date of Service" name="fds" type="date" value={formData.fds} onChange={handleChange} />
                    <InputField label="Last Date of Service" name="lds" type="date" value={formData.lds} onChange={handleChange} />
                    <InputField label="SOL Date" name="solDate" type="date" value={formData.solDate} onChange={handleChange} readOnly={true} />
                </Section>

                {['claimNo', 'adjNumber', 'doi'].map((field) => (
                    <div key={field} className="p-4 border rounded-lg bg-gray-50 mb-4">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b capitalize">{field.replace(/([A-Z])/g, ' $1')}</h3>
                        {formData[field].map((entry, index) => (
                            <div key={index} className="flex items-center gap-2 mb-2">
                                <input
                                    type="text"
                                    value={entry.value}
                                    onChange={(e) => handleMultiEntryChange(index, field, e.target.value)}
                                    className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                    placeholder={`${field.replace(/([A-Z])/g, ' $1').replace('doi', 'Date of Injury')} ${index + 1}`}
                                />
                                {formData[field].length > 1 && (
                                    <button type="button" onClick={() => removeMultiEntry(index, field)} className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-md transition-colors">-</button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={() => addMultiEntry(field)} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-3 rounded-md mt-2 transition-colors">Add Another</button>
                    </div>
                ))}

                <Section title="Case Details">
                    {/* MODIFIED: Changed InputField to SelectField */}
                    <SelectField label="Case Status" name="caseStatus" value={formData.caseStatus} onChange={handleChange} options={caseStatusOptions} />
                    <InputField label="Case Date" name="caseDate" type="date" value={formData.caseDate} onChange={handleChange} />
                    <InputField label="C&R Amount" name="crAmount" type="number" step="0.01" value={formData.crAmount} onChange={handleChange} />
                    <SelectField label="Lien Status" name="lienStatus" value={formData.lienStatus} onChange={handleChange} options={lienStatusOptions} />
                    <SelectField label="DOR Filed By" name="dorFiledBy" value={formData.dorFiledBy} onChange={handleChange} options={customDropdownOptions.dorFiledBy} />
                    <SelectField label="Status 4903.8" name="status4903_8" value={formData.status4903_8} onChange={handleChange} options={customDropdownOptions.status4903_8} />
                    <SelectField label="PMR Status" name="pmrStatus" value={formData.pmrStatus} onChange={handleChange} options={customDropdownOptions.pmrStatus} />
                    <SelectField label="Judge Order Status" name="judgeOrderStatus" value={formData.judgeOrderStatus} onChange={handleChange} options={customDropdownOptions.judgeOrderStatus} />
                </Section>

                <Section title="Hearing Information">
                    <InputField label="Hearing Status" name="hearingStatus" value={formData.hearingStatus} onChange={handleChange} />
                    <InputField label="Hearing Date" name="hearingDate" type="date" value={formData.hearingDate} onChange={handleChange} />
                    <SelectField label="Hearing Time" name="hearingTime" value={formData.hearingTime} onChange={handleChange} options={customDropdownOptions.hearingTime} />
                    <InputField label="Judge Name" name="judgeName" value={formData.judgeName} onChange={handleChange} />
                    <InputField label="Court Room Link" name="courtRoomlink" value={formData.courtRoomlink} onChange={handleChange} />
                    <InputField label="Judge Phone" name="judgePhone" value={formData.judgePhone} onChange={handleChange} />
                    <InputField label="Access Code" name="AccesCode" value={formData.AccesCode} onChange={handleChange} />
                    <InputField label="Board Location" name="boardLocation" value={formData.boardLocation} onChange={handleChange} />
                </Section>

                <Section title="Adjuster Information">
                    <InputField label="Adjuster Name" name="adjuster" value={formData.adjuster} onChange={handleChange} />
                    <InputField label="Adjuster Phone" name="adjusterPhone" value={formData.adjusterPhone} onChange={handleChange} />
                    <InputField label="Adjuster Fax" name="adjusterFax" value={formData.adjusterFax} onChange={handleChange} />
                    <InputField label="Adjuster Email" name="adjusterEmail" type="email" value={formData.adjusterEmail} onChange={handleChange} />
                </Section>

                <Section title="Defense Attorney Information">
                    <InputField label="Defense Attorney" name="defenseAttorney" value={formData.defenseAttorney} onChange={handleChange} />
                    <InputField label="Defense Attorney Phone" name="defenseAttorneyPhone" value={formData.defenseAttorneyPhone} onChange={handleChange} />
                    <InputField label="Defense Attorney Fax" name="defenseAttorneyFax" value={formData.defenseAttorneyFax} onChange={handleChange} />
                    <InputField label="Defense Attorney Email" name="defenseAttorneyEmail" type="email" value={formData.defenseAttorneyEmail} onChange={handleChange} />
                </Section>

                <div className="pt-4">
                    <label htmlFor="collector-select-manual" className="block text-sm font-medium text-gray-700">Assign to Collector (Optional)</label>
                    <select
                        id="collector-select-manual"
                        value={assignedCollector}
                        onChange={(e) => setAssignedCollector(e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                        <option value="">Do not assign</option>
                        {collectors.map(c => <option key={c._id} value={c._id}>{c.username}</option>)}
                    </select>
                </div>

                <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors">Create Record</button>
            </form>
        </div>
    );
};

export default ManualRecordForm;